import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles, Users, Trophy, Radio, Bell, Crown } from "lucide-react";
import api, { fileUrl } from "../lib/api";

/**
 * Right-hand sidebar widgets shown on Chat/Home pages.
 * - Community-Live online count
 * - Active topics in last 24h
 * - Top 5 leaderboard
 * - Next live session
 */
export default function RightSidebar() {
  const [stats, setStats] = useState(null);
  const [top, setTop] = useState([]);
  const [nextLive, setNextLive] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const [s, lb, ls] = await Promise.all([
          api.get("/stats"),
          api.get("/leaderboard?limit=5"),
          api.get("/live-sessions"),
        ]);
        if (!mounted) return;
        setStats(s.data);
        setTop(lb.data.top || []);
        const upcoming = (ls.data || []).filter((x) => x.status !== "ended").sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
        setNextLive(upcoming[0] || null);
      } catch (err) {
        // silent
      }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  return (
    <aside className="w-full lg:w-72 xl:w-80 shrink-0 space-y-3" data-testid="right-sidebar">
      {/* Community Live */}
      <Card>
        <CardTitle>Community · Live</CardTitle>
        <div className="flex items-baseline gap-2 mt-1">
          <div className="font-display text-3xl sm:text-4xl font-black text-slate-900 tracking-tighter">{stats?.online ?? "—"}</div>
          <div className="text-xs text-emerald-400 flex items-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> online
          </div>
        </div>
        <Spark />
      </Card>

      {/* Active Topics */}
      <Card>
        <CardTitle>Aktive Themen</CardTitle>
        <div className="mt-2 space-y-1.5">
          {stats?.active_topics?.length === 0 && (
            <div className="text-xs text-slate-500 py-2">Noch keine Aktivität in den letzten 24h.</div>
          )}
          {stats?.active_topics?.map((t) => (
            <div key={t.channel} className="flex items-center justify-between text-sm py-1">
              <span className="text-slate-800 truncate">#{t.name}</span>
              <span className="text-[10px] font-bold text-slate-900 bg-[#1D5BFF]/30 border border-[#1D5BFF]/40 px-2 py-0.5 rounded-full">{t.count}</span>
            </div>
          ))}
        </div>
        <Link to="/chat" className="block text-center text-[11px] font-semibold text-[#4DBFFF] hover:text-slate-900 mt-2 py-1.5 border border-slate-200 rounded-full transition">Alle Themen ansehen</Link>
      </Card>

      {/* Top Members */}
      <Card>
        <CardTitle>Top Mitglieder</CardTitle>
        <div className="mt-2 space-y-2">
          {top.length === 0 && <div className="text-xs text-slate-500 py-1">Noch keine Daten.</div>}
          {top.map((row) => (
            <Link
              to="/rangliste"
              key={row.user_id}
              data-testid={`sidebar-top-${row.rank}`}
              className="flex items-center gap-2 text-sm hover:bg-slate-100/70 rounded-lg px-1.5 py-1 -mx-1.5 transition"
            >
              <span className={`w-5 text-center font-bold ${row.rank === 1 ? "text-yellow-400" : row.rank === 2 ? "text-slate-700" : row.rank === 3 ? "text-amber-600" : "text-slate-500"}`}>{row.rank}.</span>
              <img src={fileUrl(row.avatar_url)} alt={row.name} className="w-7 h-7 rounded-full object-cover border border-slate-200 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-slate-900 text-xs font-semibold truncate">{row.name}</div>
                <div className="text-[10px] text-slate-500">{row.points} Punkte</div>
              </div>
              {row.rank === 1 && <Crown className="w-4 h-4 text-yellow-400 shrink-0" />}
            </Link>
          ))}
        </div>
        <Link to="/rangliste" className="block text-center text-[11px] font-semibold text-[#4DBFFF] hover:text-slate-900 mt-2 py-1.5 border border-slate-200 rounded-full transition">Rangliste ansehen</Link>
      </Card>

      {/* Next Live Session */}
      {nextLive && (
        <Card highlight>
          <div className="flex items-center justify-between mb-1">
            <CardTitle small>Nächste Live Session</CardTitle>
            {nextLive.status === "live" && (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/20 text-red-300 border border-red-500/40 animate-pulse flex items-center gap-1">
                <Radio className="w-2.5 h-2.5" /> LIVE
              </span>
            )}
          </div>
          <div className="font-display text-lg font-black text-slate-900">{nextLive.title}</div>
          <div className="text-xs text-slate-600 mt-1">mit {nextLive.host}</div>
          <div className="text-xs text-[#4DBFFF] mt-1">{new Date(nextLive.starts_at).toLocaleString("de-DE", { weekday: "short", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} Uhr</div>
          <Link
            to="/live"
            data-testid="sidebar-live-cta"
            className="block text-center bg-[#1D5BFF] hover:bg-[#1546C9] text-white font-bold py-2 rounded-full text-sm mt-3 shadow-[0_8px_24px_rgba(0,102,255,0.35)] flex items-center justify-center gap-1.5"
          >
            <Bell className="w-4 h-4" /> Erinnerung aktivieren
          </Link>
        </Card>
      )}
    </aside>
  );
}

function Card({ children, highlight }) {
  return (
    <div className={`rounded-2xl p-4 border ${highlight ? "bg-gradient-to-br from-[#1D5BFF]/15 to-[#4DBFFF]/5 border-[#1D5BFF]/40" : "bg-[#FFFFFF] border-slate-200"}`}>
      {children}
    </div>
  );
}

function CardTitle({ children, small }) {
  return (
    <div className={`uppercase tracking-[0.2em] text-[#4DBFFF] font-bold ${small ? "text-[9px]" : "text-[10px]"}`}>{children}</div>
  );
}

function Spark() {
  // Decorative spark line
  return (
    <svg viewBox="0 0 200 40" className="w-full h-8 mt-2 opacity-70" preserveAspectRatio="none">
      <defs>
        <linearGradient id="sparkG" x1="0" x2="1">
          <stop offset="0" stopColor="#1D5BFF" stopOpacity="0.1" />
          <stop offset="1" stopColor="#4DBFFF" stopOpacity="0.8" />
        </linearGradient>
      </defs>
      <path d="M0,30 L20,28 L40,20 L60,25 L80,12 L100,18 L120,8 L140,15 L160,5 L180,12 L200,8" fill="none" stroke="url(#sparkG)" strokeWidth="2" />
    </svg>
  );
}
